import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import { renderCellExpand } from '../../components/library/DataGrid/renderCellExpand';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridValueGetterParams,
  GridPagination,
} from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  Typography,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { Autocomplete, Button, TextField } from '../../components/library';
import { PageWrapper } from '../../styledComponents/wrappers';
import { useKeyPress } from '../../utils/hooks/useKeyPress';

interface DealerVendor {
  id: number;
  name: string;
  url: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  active: boolean;
  status: {
    id: number;
    name: string;
    description: string;
    isActiveState: boolean;
  };
  paymentType: 'paypal' | 'ach' | 'check';
  paymentTypeDetails: Record<string, any>;
  created: Date;
  modified: Date;
  createdByType: string;
  creatorName: string;
  creatorEmail: string | null;
  creatorEmailVerified: boolean;
}

interface DealerVendorStatus {
  id: number;
  name: string;
  description: string;
  isActiveState: boolean;
}

interface SearchFilters {
  searchTerm: string;
  statusId: number | null;
  active: boolean;
}

export const DEALER_MANAGEMENT_ROUTE = '/dealers';

const DealerVendorListView: React.FC = () => {
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [storeSearch, setStoreSearch] = useState<boolean>(false);
  const [dealerVendors, setDealerVendors] = useState<DealerVendor[]>([]);
  const [statuses, setStatuses] = useState<DealerVendorStatus[]>([]);
  const [resultCount, setResultCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [filters, setFilters] = useState<SearchFilters>({
    searchTerm: '',
    statusId: null,
    active: false,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  const history = useHistory();

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/tradeup/dealer-vendor-statuses`);
      setStatuses((response as AxiosResponse<DealerVendorStatus[]>).data);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const fetchData = useCallback(async () => {
    if (!searchInitiated) return;
    setLoading(true);
    const sortBy = sortModel?.[0]?.field ?? 'name';
    const sortOrder = sortModel?.[0]?.sort?.toUpperCase() ?? 'ASC';

    try {
      const response = await axios.get(`${API_URL}/tradeup/dealer-vendors`, {
        params: {
          page: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          sort: sortBy,
          sortOrder,
          searchTerm: filters.searchTerm,
          statusId: filters.statusId,
          active: filters.active,
        },
      });

      const { dealerVendors, totalCount } = (response as AxiosResponse<{
        dealerVendors: DealerVendor[];
        totalCount: number;
      }>).data;
      setDealerVendors(dealerVendors);
      setResultCount(totalCount);
    } catch (error) {
      console.error('Error fetching dealer vendors:', error);
    } finally {
      setLoading(false);
      setSearchInitiated(false);
    }
  }, [filters, paginationModel, sortModel, searchInitiated]);

  const onRowClick = (params: GridRowParams) => {
    const { id } = params;
    history.push(`${DEALER_MANAGEMENT_ROUTE}/${id}`);
  };

  const customToolbar = () => {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'right' }}>
        <GridToolbarColumnsButton />
        <GridPagination sx={{ mx: 4 }} />
      </GridToolbarContainer>
    );
  };

  const handleFilterClear = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({
      searchTerm: '',
      statusId: null,
      active: false,
    });
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchInitiated(true);
  };

  useEffect(() => {
    fetchStatuses();
    const storedColumns = window.localStorage.getItem('dealerVendorSearchColumns');
    if (storedColumns) {
      setColumnVisibilityModel(JSON.parse(storedColumns));
    }

    const storedSearch = window.localStorage.getItem('dealerVendorSearch');
    if (storedSearch) {
      setStoreSearch(true);
      setFilters(JSON.parse(storedSearch));
      setSearchInitiated(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, paginationModel, sortModel, searchInitiated]);

  useEffect(() => {
    if (storeSearch) {
      window.localStorage.setItem('dealerVendorSearch', JSON.stringify(filters));
    } else {
      window.localStorage.removeItem('dealerVendorSearch');
    }
  }, [storeSearch, filters]);

  useEffect(() => {
    window.localStorage.setItem('dealerVendorSearchColumns', JSON.stringify(columnVisibilityModel));
  }, [columnVisibilityModel]);

  useKeyPress({ activationKey: 'enter' }, () => setSearchInitiated(true));

  const columns: GridColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      renderCell: renderCellExpand,
      flex: 2,
    },
    {
      headerName: 'Status',
      field: 'status',
      valueGetter: (params: GridValueGetterParams) => ({
        name: params.row.status?.name,
        isActiveState: params.row.status?.isActiveState,
        emailVerified: params.row.creatorEmailVerified,
      }),
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as {
          name: string;
          isActiveState: boolean;
          emailVerified: boolean;
        };
        return (
          <Box>
            <Typography
              sx={{
                textTransform: 'capitalize',
                color: status.isActiveState ? 'success.main' : 'text.secondary',
              }}
            >
              {status.name}
            </Typography>
            {status.emailVerified !== undefined && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: status.emailVerified ? 'success.main' : 'warning.main',
                }}
              >
                {status.emailVerified ? '✓ Email Verified' : '⚠ Email Not Verified'}
              </Typography>
            )}
          </Box>
        );
      },
      flex: 1.5,
    },
    {
      headerName: 'Location',
      field: 'location',
      valueGetter: (params: GridValueGetterParams) => {
        const { address, address2, city, state, zip } = params.row;
        const parts = [address];
        if (address2) parts.push(address2);
        parts.push(`${city}, ${state} ${zip}`);
        return parts.join('\n');
      },
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            whiteSpace: 'pre-line',
            lineHeight: 1.2,
            py: 0.5,
          }}
        >
          {params.value}
        </Box>
      ),
      flex: 2,
    },
    {
      headerName: 'Phone',
      field: 'phone',
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      headerName: 'Payment Type',
      field: 'paymentType',
      valueGetter: (params: GridValueGetterParams) => params.value?.toUpperCase(),
      flex: 1,
    },
    {
      headerName: 'Created',
      field: 'created',
      valueGetter: (params: GridValueGetterParams) =>
        new Date(params.value).toLocaleDateString('en-US', {
          timeZone: 'America/Denver',
        }),
      flex: 1,
    },
    {
      headerName: 'Modified',
      field: 'modified',
      valueGetter: (params: GridValueGetterParams) =>
        new Date(params.value).toLocaleDateString('en-US', {
          timeZone: 'America/Denver',
        }),
      flex: 1,
    },
    {
      field: 'creator',
      headerName: 'Created By',
      flex: 1.5,
      minWidth: 200,
      valueGetter: (params: GridValueGetterParams) => {
        const row = params.row as DealerVendor;
        if (row.creatorEmail) {
          return {
            name: row.creatorName,
            email: row.creatorEmail,
          };
        }
        return { name: 'TPC' };
      },
      renderCell: (params: GridRenderCellParams) => {
        const creator = params.value as { name: string; email?: string | null };
        return (
          <Box>
            <Typography variant="body2">{creator.name}</Typography>
            {creator.email && (
              <Typography variant="caption" color="text.secondary">
                {creator.email}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <PageWrapper>
      <Grid container direction="row" spacing={2} justifyContent="start">
        <Grid container item xs={8} spacing={1}>
          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                id="searchTerm"
                value={filters.searchTerm}
                onChange={e =>
                  setFilters({
                    ...filters,
                    searchTerm: e.target.value,
                  })
                }
                placeholder="Search by name/phone/location"
                autoFocus
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs>
                  <Autocomplete
                    inputProps={{
                      label: 'Status',
                      variant: 'outlined',
                      placeholder: 'Filter by status',
                      size: 'small',
                    }}
                    getOptionLabel={status => status?.name || ''}
                    options={statuses}
                    value={statuses.find(status => status.id === filters.statusId) || null}
                    onChange={(_e, option) => {
                      setFilters(prevState => ({ ...prevState, statusId: option?.id ?? null }));
                    }}
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    value="start"
                    control={
                      <Checkbox
                        onChange={e => {
                          setFilters(prevState => ({
                            ...prevState,
                            active: e.target.checked,
                          }));
                        }}
                        name="active"
                        checked={filters.active}
                        color="success"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Active</Typography>}
                    labelPlacement="start"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container item xs={4} spacing={1}>
          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12} xl={6}>
              <Button
                fullWidth
                ordinality="secondary"
                disabled={loading}
                variant="contained"
                endIcon={loading && <CircularProgress color={'inherit'} size={'16px'} />}
                onClick={handleFilterSubmit}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} xl={6}>
              <Button
                fullWidth
                ordinality="secondary"
                disabled={loading}
                variant="text"
                onClick={handleFilterClear}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12}>
              <FormControlLabel
                value="start"
                control={
                  <Switch
                    onChange={e => setStoreSearch(e.target.checked)}
                    name="storeSearch"
                    checked={storeSearch}
                    color="success"
                  />
                }
                label={<Typography variant="body2">Reload Search</Typography>}
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box sx={{ mx: 'auto', my: 2 }}>
        <StripedDataGrid
          sx={{
            height: !dealerVendors.length ? '30rem' : 'auto',
            my: 3,
          }}
          searchNotInitiated={!searchInitiated}
          rows={dealerVendors}
          rowCount={resultCount}
          loading={loading}
          onRowClick={onRowClick}
          disableDensitySelector
          disableColumnFilter
          disableColumnMenu
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={model => {
            setPaginationModel(model);
            setSearchInitiated(true);
          }}
          pageSizeOptions={[25]}
          sortingMode="server"
          sortingOrder={['desc', 'asc', null]}
          sortModel={sortModel}
          onSortModelChange={model => {
            setSortModel(model);
            setSearchInitiated(true);
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
          columns={columns}
          slots={{
            toolbar: customToolbar,
          }}
        />
      </Box>
    </PageWrapper>
  );
};

export default DealerVendorListView;
